import gsap from 'gsap';
import { type RefObject, useEffect, useRef } from 'react';

import { DURATION, EASE } from '@animations/index';
import { usePrefersReducedMotion } from '@stores/index';

/** How far the element stretches along its travel, and flattens across it. */
const STRETCH = { scaleX: 1.16, scaleY: 0.9 } as const;

/**
 * Stretches an element along its travel when `value` changes, then eases
 * it back into shape: the way a drop of liquid elongates as it is pushed.
 * No wobble: the settle is a single smooth curve.
 *
 * Runs alongside whatever moves the element (here, a CSS translate), so the
 * stretch reads as a consequence of the movement rather than an effect of
 * its own. Uses `transform` scale, which composes with the CSS `translate`
 * property instead of overwriting it.
 *
 * Compares against the previous value rather than skipping the first run,
 * so StrictMode's replayed effect in development is not mistaken for a
 * change. Off under reduced motion.
 */
export const useSquashOnChange = (ref: RefObject<HTMLElement | null>, value: unknown): void => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const previous = useRef(value);

  useEffect(() => {
    if (Object.is(previous.current, value)) return;
    previous.current = value;

    const element = ref.current;
    if (!element || prefersReducedMotion) return;

    const timeline = gsap
      .timeline()
      .to(element, { ...STRETCH, duration: DURATION.fast, ease: EASE.out })
      .to(element, { scaleX: 1, scaleY: 1, duration: DURATION.base, ease: EASE.out });

    return () => {
      timeline.kill();
      gsap.set(element, { clearProps: 'scale' });
    };
  }, [ref, value, prefersReducedMotion]);
};
